import { computed, ComputedRef } from 'vue'
import { CMS, MappedAttributeMultiCheckboxFilter, Products } from '@one/types'

interface DefaultSeoData {
  title: string;
  description: string;
}

export default function useSeoMethods(vm) {
  const DIVIDER = {
    DOT: ' \u2022 ',
    DASH: ' - ',
    COMMA: ', ',
    WHITESPACE: ' ',
    SEMICOLON: '; ',
  }

  const configuration: ComputedRef<CMS.Responses.MainConfigurationQuery | null> = computed(() => vm.$store.getters['cms/configuration'])
  const getSelectedAttributes = computed(() => vm.$store.getters['products/getSelectedAttributes'])
  const getFilter = (id: string) => vm.$store.getters['products/getFilter'](id)

  const defaultSeoData: ComputedRef<DefaultSeoData> = computed((): DefaultSeoData => {
    const { title, description } = configuration.value?.seo || {}
    return {
      title:
        title ||
        vm.$t('default_seo.title') ||
        vm.$tenantConfig.tenantKey,
      description:
        description || (vm.$t('default_seo.description') as string),
    }
  })

    const filtersSeo: ComputedRef<string | undefined> = computed((): string | undefined => {
      const selectedFilter: Array<MappedAttributeMultiCheckboxFilter> =
        Object.keys(getSelectedAttributes)
          .slice(0, 2)
          .map((el: string) => getFilter(el))
      return (
        structuredSeoTitle(
          DIVIDER.SEMICOLON,
          selectedFilter.map(
            (el: MappedAttributeMultiCheckboxFilter) =>
              `${el.name}: ${structuredSeoTitle(
                DIVIDER.COMMA,
                Object.keys(el.items)
                  .filter((item: string) => el.items[item].selected)
                  .map((item: string) => el.items[item].value),
              )}`,
          ),
        ) || undefined
      )
    })

    const defaultSeoRobots: ComputedRef<string> = computed((): string => {
      return vm.$tenantConfig.isDev
        ? 'noindex, nofollow'
        : 'index, follow'
    })

    const structuredSeoTitle = (
      separator: string = DIVIDER.DASH,
      payload: Array<string | undefined | null> = [],
    ): string => {
      return payload.filter(Boolean).join(`${separator}`)
    }

    const robotsSeo = (seo: Products.ProductSeo | undefined): string => {
      const robots: Array<string> = []
      if (typeof seo?.index === 'boolean') {
        robots.push(seo?.index ? 'index' : 'noindex')
      }
      if (typeof seo?.follow === 'boolean') {
        robots.push(seo?.follow ? 'follow' : 'nofollow')
      }
      return robots.join(', ')
    }

    return {
      DIVIDER,
      defaultSeoData,
      filtersSeo,
      defaultSeoRobots,
      structuredSeoTitle,
      robotsSeo,
    }
}
